import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';
import { 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  CheckCircle, 
  Clock,
  Target,
  Users,
  Globe,
  Building2
} from 'lucide-react';
import { materialityService } from '@/utils/materialityService';
import { MaterialityTopic } from '@/types/materiality';

interface MaterialityOverviewProps {}

export const MaterialityOverview: React.FC<MaterialityOverviewProps> = () => {
  const [topics, setTopics] = useState<MaterialityTopic[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'environmental' | 'social' | 'governance'>('all');

  // Fetch data from database
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log('Fetching materiality topics...');
        const topicsData = await materialityService.getMaterialityTopics();
        console.log('Received materiality topics:', topicsData);
        setTopics(topicsData);
      } catch (error) {
        console.error('Error fetching materiality topics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Function to refresh data (can be called from parent components)
  const refreshData = async () => {
    try {
      setLoading(true);
      // Clear cache to force fresh data fetch
      materialityService.clearCache();
      const topicsData = await materialityService.getMaterialityTopics();
      setTopics(topicsData);
    } catch (error) {
      console.error('Error refreshing materiality topics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Expose refresh function to parent
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).refreshMaterialityData = refreshData;
    }
  }, []);

  const filteredTopics = selectedCategory === 'all' 
    ? topics 
    : topics.filter(topic => topic.category === selectedCategory);

  const highPriorityTopics = topics.filter(topic => topic.priority === 'high');
  const activeTopics = topics.filter(topic => topic.status === 'active');
  const monitoringTopics = topics.filter(topic => topic.status === 'monitoring');

  const categoryData = [
    { name: 'Environmental', value: topics.filter(t => t.category === 'environmental').length, color: '#10b981' },
    { name: 'Social', value: topics.filter(t => t.category === 'social').length, color: '#3b82f6' },
    { name: 'Governance', value: topics.filter(t => t.category === 'governance').length, color: '#8b5cf6' }
  ];

  const priorityData = [
    { name: 'High', value: highPriorityTopics.length, color: '#ef4444' },
    { name: 'Medium', value: topics.filter(t => t.priority === 'medium').length, color: '#f59e0b' },
    { name: 'Low', value: topics.filter(t => t.priority === 'low').length, color: '#10b981' }
  ];

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-blue-100 text-blue-800';
      case 'monitoring': return 'bg-orange-100 text-orange-800';
      case 'addressed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Topics</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{topics.length}</div>
            <p className="text-xs text-muted-foreground">
              Material topics identified
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">High Priority</CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{highPriorityTopics.length}</div>
            <p className="text-xs text-muted-foreground">
              Require immediate attention
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Topics</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{activeTopics.length}</div>
            <p className="text-xs text-muted-foreground">
              Currently being addressed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monitoring</CardTitle>
            <Clock className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{monitoringTopics.length}</div>
            <p className="text-xs text-muted-foreground">
              Under continuous monitoring
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Category Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Topics by Category</CardTitle>
            <CardDescription>Distribution of material topics across ESG categories</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={categoryData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {categoryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Priority Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Topics by Priority</CardTitle>
            <CardDescription>Distribution of topics by priority level</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={priorityData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Filter and Topics List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Material Topics</CardTitle>
              <CardDescription>
                Comprehensive list of identified material topics with their assessment details
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant={selectedCategory === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory('all')}
              >
                All
              </Button>
              <Button
                variant={selectedCategory === 'environmental' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory('environmental')}
              >
                Environmental
              </Button>
              <Button
                variant={selectedCategory === 'social' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory('social')}
              >
                Social
              </Button>
              <Button
                variant={selectedCategory === 'governance' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory('governance')}
              >
                Governance
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredTopics.map((topic) => (
              <div key={topic.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold">{topic.name}</h3>
                      <Badge className={getPriorityColor(topic.priority)}>
                        {topic.priority} Priority
                      </Badge>
                      <Badge className={getStatusColor(topic.status)}>
                        {topic.status}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                      <div>
                        <p className="text-sm text-muted-foreground">Business Impact</p>
                        <div className="flex items-center gap-2">
                          <Progress value={topic.businessImpact * 20} className="flex-1" />
                          <span className="text-sm font-medium">{topic.businessImpact}/5</span>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Stakeholder Importance</p>
                        <div className="flex items-center gap-2">
                          <Progress value={topic.stakeholderImportance * 20} className="flex-1" />
                          <span className="text-sm font-medium">{topic.stakeholderImportance}/5</span>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Stakeholders</p>
                        <p className="font-medium">{topic.stakeholders}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Actions</p>
                        <p className="font-medium">{topic.actions}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span>Last Assessment: {new Date(topic.lastAssessment).toLocaleDateString()}</span>
                      <span>Next Review: {new Date(topic.nextReview).toLocaleDateString()}</span>
                    </div>
                  </div>
                  
                  <Button variant="outline" size="sm">
                    View Details
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}; 